import type {
  MeFeatures,
  ObjectModelListQuery,
  ObjectModelUpdate,
  Terrain,
} from "@divespots/contracts";
import {
  QueryClient,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "./client.ts";
import type { ModelUploadParams } from "./client.ts";
import { isApiRequestError } from "./errors.ts";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // Without a connection the service worker may still answer (saved spots, cached lists), so
        // queries must run instead of waiting for "online". A failed request is reported at once.
        networkMode: "always",
        // Only transient failures are retried; 400/401/403/404 show their error UI at once.
        retry: (count, error) =>
          (typeof navigator === "undefined" || navigator.onLine) &&
          isApiRequestError(error) &&
          (error.code === "network" ||
            error.code === "rate_limited" ||
            error.code === "internal") &&
          count < 2,
      },
    },
  });
}

export const keys = {
  terrain: (lat: number, lon: number, radius: number) => ["terrain", lat, lon, radius] as const,
  lakes: ["lakes"] as const,
  spots: ["spots"] as const,
  spot: (slug: string) => ["spot", slug] as const,
  meFeatures: ["me", "features"] as const,
  diveLakes: ["dive", "lakes"] as const,
  objectModels: (q: ObjectModelListQuery) => ["object-models", q] as const,
  /** Content-addressed (sha256): never stale, and kept outside the list root so list refreshes skip it. */
  objectModelFile: (sha: string | undefined) => ["object-model-file", sha] as const,
  lakeOverview: (slug: string) => ["lake-overview", slug] as const,
  wind: ["wind"] as const,
  precip: ["precip"] as const,
};

export const useLakes = () =>
  useQuery({
    queryKey: keys.lakes,
    queryFn: ({ signal }) => api.listLakes(signal).then((r) => r.lakes),
  });

export const useSpots = () =>
  useQuery({
    queryKey: keys.spots,
    queryFn: ({ signal }) => api.listSpots({ status: "published" }, signal).then((r) => r.spots),
  });

export const useSpot = (slug: string | undefined) =>
  useQuery({
    queryKey: keys.spot(slug ?? ""),
    queryFn: ({ signal }) => api.getSpot(slug as string, signal),
    enabled: Boolean(slug),
  });

/** Lakebed tile around a point. Never auto-retried: 404/429 have their own UI with a manual retry. */
export const useTerrain = (lat: number, lon: number, radius: number, enabled = true) =>
  useQuery({
    queryKey: keys.terrain(lat, lon, radius),
    queryFn: ({ signal }) => api.getTerrain({ lat, lon, radius }, signal),
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    // switching the radius keeps the current tile on screen until the next one has arrived
    placeholderData: keepPreviousData,
  });

/**
 * The wind forecast grid, only while the overlay is on and the device is online. Fresh for 10 min,
 * refetched every 30 min while on. Failures (503 until the API has a field) are not retried here:
 * the time bar shows "temporarily unavailable" and the next refetch tries again.
 */
export const useWindField = (enabled: boolean) =>
  useQuery({
    queryKey: keys.wind,
    queryFn: ({ signal }) => api.windField(signal),
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    refetchInterval: enabled ? 30 * 60_000 : false,
  });

/** The precipitation grid: same settings as the wind field, enabled only while its overlay is on. */
export const usePrecipField = (enabled: boolean) =>
  useQuery({
    queryKey: keys.precip,
    queryFn: ({ signal }) => api.precipField(signal),
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    refetchInterval: enabled ? 30 * 60_000 : false,
  });

const NO_FEATURES: MeFeatures = { dive: false };

export interface MeFeaturesState extends MeFeatures {
  isPending: boolean;
  /** The request failed for a reason other than 401/403 (server, network, offline). */
  isError: boolean;
  /** The server says there is no session (401), whatever the cached `/api/me` thinks. */
  signedOut: boolean;
  refetch: () => void;
}

/**
 * What the signed-in caller may use (`/api/me/features`). Short staleTime: a grant or revoke
 * applies on the next visit. A 403 (banned) means no features; a 401 sets `signedOut`; any other
 * failure is `isError`, never "no access".
 */
export const useMeFeatures = (enabled = true): MeFeaturesState => {
  const q = useQuery({
    queryKey: keys.meFeatures,
    queryFn: async ({ signal }): Promise<MeFeatures & { signedOut: boolean }> => {
      try {
        return { ...(await api.meFeatures(signal)), signedOut: false };
      } catch (e) {
        if (isApiRequestError(e) && e.code === "unauthorized")
          return { ...NO_FEATURES, signedOut: true };
        if (isApiRequestError(e) && e.code === "forbidden")
          return { ...NO_FEATURES, signedOut: false };
        throw e;
      }
    },
    enabled,
    retry: false,
    staleTime: 30_000,
  });
  return {
    dive: enabled ? (q.data?.dive ?? false) : false,
    signedOut: enabled ? (q.data?.signedOut ?? false) : false,
    isPending: enabled && q.isPending,
    isError: enabled && q.isError,
    refetch: () => void q.refetch(),
  };
};

/** Lakes with a built dive tile set; only ask when the caller has dive access. */
export const useDiveLakes = (enabled: boolean) =>
  useQuery({
    queryKey: keys.diveLakes,
    queryFn: ({ signal }) => api.listDiveLakes(signal).then((r) => r.lakes),
    enabled,
    retry: false,
    staleTime: 60_000,
  });

/**
 * The lake's overview terrain for the mini-map. A lake without one (404) resolves to null, so the
 * map is simply absent; any other failure is an error and the map is absent too. Never retried.
 */
export const useLakeOverview = (slug: string | null | undefined) =>
  useQuery({
    queryKey: keys.lakeOverview(slug ?? ""),
    queryFn: async ({ signal }): Promise<Terrain | null> => {
      try {
        return await api.lakeOverview(slug as string, signal);
      } catch (e) {
        if (isApiRequestError(e) && e.code === "not_found") return null;
        throw e;
      }
    },
    enabled: Boolean(slug),
    retry: false,
    staleTime: 30 * 60_000,
  });

/** Models of the objects the caller may see in a spot or a lake (exactly one of `spotId` / `lake`). */
export const useObjectModels = (query: ObjectModelListQuery | null) =>
  useQuery({
    queryKey: keys.objectModels(query ?? { spotId: "" }),
    queryFn: ({ signal }) =>
      api.listObjectModels(query as ObjectModelListQuery, signal).then((r) => r.models),
    enabled: query !== null,
    retry: false,
  });

/** Admin writes for one object's model; every success refreshes the model lists. */
export function useObjectModelMutations(objectId: string) {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ["object-models"] });
  return {
    upload: useMutation({
      mutationFn: ({ file, ...p }: { file: ArrayBuffer } & ModelUploadParams) =>
        api.putObjectModel(objectId, file, p),
      onSuccess: (model, { file }) => {
        qc.setQueryData(keys.objectModelFile(model.sha256), file);
        return done();
      },
    }),
    update: useMutation({
      mutationFn: (body: ObjectModelUpdate) => api.updateObjectModel(objectId, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: () => api.deleteObjectModel(objectId),
      onSuccess: done,
    }),
  };
}
